// src/pages/dashboard/header/DashboardHeader.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
    Typography,
    Box,
    Paper,
    Stack,
    CircularProgress,
    Tooltip,
    Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// Import utility functions
import { getUserName, getUserTitle } from "../userDataUtils";

// Import fungsi tanggal yang sudah dimodularisasi
import { getGreeting } from "../dateUtils";

function DashboardHeader({
    user,
    getUserData,
    selectedDate,
    dateInfo,
    calendarOpen,
    onCalendarClick,
    formatDate,
    onFilterClick // Tambahkan prop baru untuk filter toggle
}) {
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [dashboardHeader, setDashboardHeader] = useState("FPMS Dashboard Overview");
    const [filterOpen, setFilterOpen] = useState(false); // State untuk status filter

    // Fungsi utama untuk update header
    const updateDashboardHeader = useCallback(() => {
        if (!user) return;

        const cachedData = getUserData();
        const greeting = getGreeting();
        const title = getUserTitle(cachedData?.gender);
        const userName = getUserName(user, cachedData);

        const header = `${greeting} ${title} ${userName}`.replace(/\s+/g, " ").trim();
        setDashboardHeader(header);

        console.log("Dashboard header updated:", header);
    }, [user, getUserData]);

    // Update header setiap kali user berubah ATAU komponen dimount
    useEffect(() => {
        updateDashboardHeader();

        // Tambahkan event listener untuk menangani perubahan route/navigasi
        const handleRouteChange = () => {
            console.log("Route changed, updating dashboard header...");
            updateDashboardHeader();
        };

        // Event listener untuk popstate (back/forward navigation)
        window.addEventListener("popstate", handleRouteChange);

        // Untuk Single Page Applications, tambahkan listener untuk custom events
        window.addEventListener("dashboard-update", updateDashboardHeader);

        return () => {
            window.removeEventListener("popstate", handleRouteChange);
            window.removeEventListener("dashboard-update", updateDashboardHeader);
        };
    }, [updateDashboardHeader]);

    const handleRefresh = () => {
        setRefreshLoading(true);
        setTimeout(() => {
            setRefreshLoading(false);
            // Panggil fungsi update header
            updateDashboardHeader();

            // Jika perlu memicu update di komponen lain, bisa dispatch custom event
            window.dispatchEvent(new Event("dashboard-update"));
        }, 1000);
    };

    // Handler untuk toggle filter
    const handleFilterClick = () => {
        const newFilterState = !filterOpen;
        setFilterOpen(newFilterState);
        
        // Panggil callback jika ada
        if (onFilterClick) {
            onFilterClick(newFilterState);
        }
        
        console.log("Filter toggled:", newFilterState);
    };

    // Shadow style yang konsisten
    const paperShadow =
        "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)";

    return (
        <Box sx={{ mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {dashboardHeader}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Monitor performa & statistik operasional komoditas nanas segar
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    {/* Chip dengan informasi tanggal - tanpa border */}
                    <Chip
                        icon={<CalendarTodayIcon />}
                        label={`${dateInfo.display}, Week: ${dateInfo.week}`}
                        deleteIcon={
                            <ArrowDropDownIcon
                                sx={{
                                    transform: calendarOpen ? "rotate(180deg)" : "rotate(0deg)",
                                    transition: "transform 0.3s ease",
                                }}
                            />
                        }
                        onDelete={onCalendarClick}
                        sx={{
                            height: 36,
                            borderRadius: 2,
                            backgroundColor: "background.paper",
                            boxShadow: paperShadow,
                            "& .MuiChip-label": {
                                px: 2,
                                py: 1.5,
                                fontWeight: 500,
                            },
                            "& .MuiChip-icon": {
                                ml: 1.5,
                                color: "primary.main",
                            },
                            "& .MuiChip-deleteIcon": {
                                mr: 1,
                                color: "text.secondary",
                            },
                            "&:hover": {
                                backgroundColor: "action.hover",
                                boxShadow:
                                    "0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)",
                            },
                        }}
                        onClick={onCalendarClick}
                    />

                    {/* Icon untuk toggle filter - tanpa border */}
                    <Tooltip title={filterOpen ? "Sembunyikan Filter" : "Tampilkan Filter"}>
                        <Paper
                            component="button"
                            onClick={handleFilterClick}
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                backgroundColor: "background.paper",
                                boxShadow: paperShadow,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                p: 0,
                                m: 0,
                                border: "none",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                    boxShadow:
                                        "0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)",
                                },
                            }}
                        >
                            {filterOpen ? (
                                <FilterAltIcon color="primary" />
                            ) : (
                                <FilterAltOffIcon />
                            )}
                        </Paper>
                    </Tooltip>

                    {/* Icon untuk refresh data - tanpa border */}
                    <Tooltip title="Refresh Data">
                        <Paper
                            component="button"
                            onClick={handleRefresh}
                            disabled={refreshLoading}
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                backgroundColor: "background.paper",
                                boxShadow: paperShadow,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: refreshLoading ? "not-allowed" : "pointer",
                                p: 0,
                                m: 0,
                                border: "none",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                    boxShadow:
                                        "0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)",
                                },
                                "&:disabled": {
                                    opacity: 0.5,
                                    boxShadow: "none",
                                },
                            }}
                        >
                            {refreshLoading ? <CircularProgress size={24} /> : <RefreshIcon color="primary" />}
                        </Paper>
                    </Tooltip>
                </Stack>
            </Stack>
        </Box>
    );
}

export default DashboardHeader;